import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "primenax_cart_v1";

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

function reducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const { product, quantity = 1 } = action;
      const existing = state.find((i) => i.product_id === product.id);
      if (existing) {
        return state.map((i) =>
          i.product_id === product.id
            ? { ...i, quantity: Math.min(20, i.quantity + quantity) }
            : i
        );
      }
      return [
        ...state,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity,
        },
      ];
    }
    case "UPDATE": {
      const { product_id, quantity } = action;
      if (quantity <= 0)
        return state.filter((i) => i.product_id !== product_id);
      return state.map((i) =>
        i.product_id === product_id ? { ...i, quantity } : i
      );
    }
    case "REMOVE":
      return state.filter((i) => i.product_id !== action.product_id);
    case "CLEAR":
      return [];
    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [items, dispatch] = useReducer(reducer, null, loadInitial);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const value = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return {
      items,
      count,
      subtotal,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      toggleCart: () => setIsOpen((o) => !o),
      addItem: (product, quantity = 1) => {
        dispatch({ type: "ADD", product, quantity });
        setIsOpen(true);
      },
      updateItem: (product_id, quantity) =>
        dispatch({ type: "UPDATE", product_id, quantity }),
      removeItem: (product_id) => dispatch({ type: "REMOVE", product_id }),
      clearCart: () => dispatch({ type: "CLEAR" }),
    };
  }, [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
